import { Box, Chip, Divider, Grid, Stack, Typography } from '@mui/material';
import { ITreatmentOrderDTO } from 'API/Treatment/treatment';
import { LinkArrowLeft2 } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { format } from 'date-fns';

interface IOrderCard {
  order: ITreatmentOrderDTO;
}

enum FINE {
  HAS_FINE = 1,
}

export const OrderCard = ({ order }: IOrderCard) => {
  const dictionary = useDictionary(Dictionary, DICTIONARY.Treatment);
  const isDesktop = useIsDesktop();

  const statusLabelBackgroundColor = {
    [dictionary.status.cancelled]: 'orange.shade_100',
    [dictionary.status.previous]: '#E0F6EF',
    [dictionary.status.future]: 'primary.shade_100',
  };

  const formattedOrderNumber =
    order.startDate &&
    new Date(order.startDate).getFullYear().toString()?.slice(2, 4) +
      '-' +
      order.orderNumberSupplier;

  return (
    <Stack
      bgcolor={'#fff'}
      borderRadius={'16px'}
      border={'1px solid'}
      borderColor={'primary.shade_100'}
      position={'relative'}
    >
      {order.statusLabel && (
        <Box position={'absolute'}>
          <Chip
            label={order.statusLabel}
            sx={{
              backgroundColor: statusLabelBackgroundColor[order.statusLabel],
              borderRadius: '16px 0px',
              padding: '6px 24px',
              fontSize: '16px',
              fontWeight: 600,
            }}
          />
        </Box>
      )}
      <Stack
        padding={isDesktop ? '44px 24px 32px 24px' : '44px 16px 24px 16px'}
      >
        <Stack gap={'4px'}>
          <Typography variant={'body4'}>{order.hotelName}</Typography>
          <Typography variant={'body7'}>{order.medicinalHotSprings}</Typography>
        </Stack>
        <Divider
          aria-hidden={'true'}
          orientation='horizontal'
          sx={{
            borderColor: 'primary.shade_100',
            marginY: isDesktop ? '16px' : '12px',
          }}
        />
        <Grid container gap={isDesktop ? '50px' : '20px 24px'}>
          {order.orderNumberSupplier && (
            <Grid item>
              <Stack gap={'4px'} minWidth={isDesktop ? '104px' : '275px'}>
                <Typography variant={'body7'}>
                  {dictionary.recentOrders.orderNumber}
                </Typography>
                <Typography variant={'body5'}>
                  {formattedOrderNumber}
                </Typography>
              </Stack>
            </Grid>
          )}
          {order.supplier.supplierName && (
            <Grid item>
              <Stack gap={'4px'} minWidth={'136px'}>
                <Typography variant={'body7'}>
                  {dictionary.recentOrders.supplierName}
                </Typography>
                <Typography variant={'body5'}>
                  {order.supplier.supplierName}
                </Typography>
              </Stack>
            </Grid>
          )}
          {order.startDate && (
            <Grid item>
              <Stack gap={'4px'} minWidth={'104px'}>
                <Typography variant={'body7'}>
                  {dictionary.recentOrders.startDate}
                </Typography>
                <Typography variant={'body5'}>
                  {format(new Date(order.startDate), 'dd.MM.yyyy')}
                </Typography>
              </Stack>
            </Grid>
          )}

          {order.daysNumber && (
            <Grid item>
              <Stack gap={'4px'} minWidth={isDesktop ? '80px' : '136px'}>
                <Typography variant={'body7'}>
                  {dictionary.recentOrders.nightsNumber}
                </Typography>
                <Typography variant={'body5'}>{order.daysNumber}</Typography>
              </Stack>
            </Grid>
          )}
          <Grid item>
            <Stack gap={'4px'} minWidth={'120px'}>
              <Typography variant={'body7'}>
                {dictionary.recentOrders.attendantsNumber}
              </Typography>
              <Typography variant={'body5'}>
                {order.accompaniedNumber}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
        {!order.status && (
          <>
            <Divider
              orientation='horizontal'
              aria-hidden={'true'}
              sx={{
                borderColor: 'primary.shade_100',
                marginY: isDesktop ? '16px' : '12px',
              }}
            />
            <Stack direction={isDesktop ? 'row' : 'column'} gap={'4px'}>
              <Stack gap={'4px'} minWidth={'142px'} width={'auto'}>
                <Typography variant={'body7'}>
                  {dictionary.recentOrders.cancelOrderLabel}
                </Typography>
                <Typography variant={'body5'}>
                  {order.fine === FINE.HAS_FINE
                    ? dictionary.recentOrders.penalty
                    : dictionary.recentOrders.noPanalty}
                </Typography>
              </Stack>
              <LinkArrowLeft2
                href={dictionary.recentOrders.infoAboutCancelationUrl}
                newWindow={true}
                text={dictionary.recentOrders.infoAboutCancelation}
                sx={{
                  alignSelf: isDesktop ? 'end' : 'start',
                  margin: isDesktop ? '0 0 0 auto' : '0',
                }}
              />
            </Stack>
          </>
        )}
      </Stack>
    </Stack>
  );
};
